import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import { ArrowBack } from '@mui/icons-material';
import api from 'services/api';
import { cpfMask } from 'utils/cpf';
import { useAlert } from 'contexts/alert';
import {
  AddressableView,
  IBeneficiary,
  IDistribution,
  IStatusGeral,
  IAPIResponse,
  ICampaign,
  IAPIBeneficiary,
} from './Addressable.types';
import { AddressableCampaignSend } from './AddressableCampaignSend';
import { AddressableCampaignStatus } from './AddressableCampaignStatus';
import { AddressableCampaignBeneficiaries } from './AddressableCampaignBeneficiaries';
import { Container, Button as StyledButton } from './Addressable.styled';

interface IAddressableProps {
  organizationId: string;
}

// Helper function to format date from backend
const formatHiringDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    // Formato esperado: "2025-05-07 00:00:00.000"
    const datePart = dateString.split(' ')[0]; // Pega apenas a parte da data
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return '-';
  }
};

export function Addressable({ organizationId }: IAddressableProps) {
  const { openAlert } = useAlert();
  const [currentView, setCurrentView] = useState<AddressableView>('main');
  const [campaignTab, setCampaignTab] = useState<'SEND_CAMPAIGNS' | 'CHECK_CAMPAIGNS'>('SEND_CAMPAIGNS');
  const [isSaving, setIsSaving] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<ICampaign | null>(null);
  const [campaigns, setCampaigns] = useState<ICampaign[]>([]);

  // Fetch data from API
  const { data: apiData, isFetching, isError, error } = useQuery({
    queryKey: ['addressable', organizationId],
    queryFn: () => api.get<{ data: IAPIResponse }>(`/v2/addressable/organizations/${organizationId}`),
    select: (response) => response.data.data,
  });

  // Map API data to component format
  const statusGeral = useMemo<IStatusGeral>(() => {
    if (!apiData?.metrics?.generalMetrics) {
      return { definidos: 0, aguardandoDefinicao: 0, total: 0 };
    }

    const definidos = apiData.metrics.generalMetrics.find((m) => m.name === 'Definidos')?.value || 0;
    const aguardandoDefinicao = apiData.metrics.generalMetrics.find((m) => m.name === 'Aguardando definição')?.value || 0;

    return {
      definidos,
      aguardandoDefinicao,
      total: definidos + aguardandoDefinicao,
    };
  }, [apiData]);

  const distributions = useMemo<IDistribution[]>(() => {
    if (!apiData?.metrics?.workspaceMetrics) return [];

    return apiData.metrics.workspaceMetrics.map((metric) => ({
      name: metric.name,
      count: metric.value,
    }));
  }, [apiData]);

  const [beneficiaries, setBeneficiaries] = useState<IBeneficiary[]>([]);

  // Beneficiários que ainda não têm workspace definido (para a tela de definir distribuição)
  const beneficiariesToDefine = useMemo(() => {
    return beneficiaries.filter((beneficiary) => !beneficiary.workspaceIdFromBackend);
  }, [beneficiaries]);

  // Update beneficiaries when API data changes
  useEffect(() => {
    if (!apiData?.beneficiaries) return;

    const mappedBeneficiaries: IBeneficiary[] = apiData.beneficiaries.map((apiBeneficiary) => ({
      id: apiBeneficiary.EMPLOYEE_ID,
      name: apiBeneficiary.PERSON_NAME || 'Sem nome',
      cpfBeneficiario: apiBeneficiary.PERSON_TAX_PAYER_ID ? cpfMask(apiBeneficiary.PERSON_TAX_PAYER_ID) : '',
      cpfTitular: apiBeneficiary.EMPLOYEE_TAX_PAYER_ID ? cpfMask(apiBeneficiary.EMPLOYEE_TAX_PAYER_ID) : '',
      cargo: apiBeneficiary.EMPLOYEE_POSITION || 'Não informado',
      regiao: `${apiBeneficiary.EMPLOYEE_CITY_ID || ''} - ${apiBeneficiary.EMPLOYEE_STATE_ID || ''}`.trim(),
      dataContratacao: formatHiringDate(apiBeneficiary.EMPLOYEE_HIRING_DATE),
      distribuicao: '', // Sempre começa vazio - usuário seleciona manualmente
      workspaceIdFromBackend: apiBeneficiary.workspaceId || '', // Guarda o valor do backend
      assignedWorkspaceName: apiBeneficiary.assignedWorkspaceName, // Nome formatado vindo do backend
      plans: apiBeneficiary.plans || [],
    }));

    setBeneficiaries(mappedBeneficiaries);
  }, [apiData]);

  // Handler para quando uma campanha é enviada
  const handleCampaignSent = (selectedBeneficiaries: IAPIBeneficiary[]) => {
    // Criar campanha mockada
    const newCampaign: ICampaign = {
      id: `campaign-${Date.now()}`,
      type: 'MISSING_DATA',
      sentDate: new Date(),
      finishedDate: null,
      status: 'PENDING',
      metricTotal: String(selectedBeneficiaries.length),
      metricPending: String(selectedBeneficiaries.length),
      metricDone: '0',
      metrics: [
        { label: 'Total', value: String(selectedBeneficiaries.length) },
        { label: 'Pendentes', value: String(selectedBeneficiaries.length) },
        { label: 'Concluídas', value: '0' },
      ],
      beneficiaries: selectedBeneficiaries.map((b) => ({
        name: b.PERSON_NAME || 'Sem nome',
        taxPayerId: b.PERSON_TAX_PAYER_ID,
        taxPayerIdEmployee: b.EMPLOYEE_TAX_PAYER_ID,
        legalEntityTaxId: b.ORGANIZATION_ID,
        type: b.DEPENDENT_TYPE_ID ? 'dependent' : 'employee',
        sentDate: new Date(),
        finishedDate: null,
        campaignUrl: `https://forms.conducco.com/campaign/${Date.now()}/beneficiary/${b.PERSON_TAX_PAYER_ID}`,
        errorMessage: null,
        status: 'PENDING',
      })),
      latestUpdateDate: new Date(),
    };

    setCampaigns((prev) => [newCampaign, ...prev]);
    setCampaignTab('CHECK_CAMPAIGNS');
  };

  // Handler para voltar da tela de detalhe
  const handleDetailBack = () => {
    setSelectedCampaign(null);
  };

  // Verifica se está na tela de detalhe
  const isDetailPage = selectedCampaign !== null;

  // Define título baseado no estado
  const getCampaignPageTitle = () => {
    if (isDetailPage) {
      return 'Campanha em andamento';
    }
    return 'Campanhas';
  };

  // Define ação do botão back baseado no estado
  const getCampaignBackAction = () => {
    if (isDetailPage) {
      return handleDetailBack;
    }
    return () => setCurrentView('main');
  };

  // Handlers
  const handleDistributionChange = (beneficiaryId: string, newDistribution: string) => {
    setBeneficiaries((prev) =>
      prev.map((b) => (b.id === beneficiaryId ? { ...b, distribuicao: newDistribution } : b))
    );
  };

  const handleClearDistributions = () => {
    // Limpar seleções manuais feitas pelo usuário
    setBeneficiaries((prev) =>
      prev.map((b) => ({
        ...b,
        distribuicao: '', // Limpa as seleções do usuário
      }))
    );
  };

  const handleSaveDistributions = async () => {
    try {
      setIsSaving(true);

      // Filtrar apenas beneficiários com distribuição definida e mapear para o formato da API
      const distributionsToSave = beneficiaries
        .filter((beneficiary) => beneficiary.distribuicao && beneficiary.distribuicao.trim() !== '')
        .map((beneficiary) => ({
          federalTaxId: beneficiary.cpfBeneficiario.replace(/\D/g, ''), // Remove máscara do CPF
          workspaceId: beneficiary.distribuicao,
        }));

      if (distributionsToSave.length === 0) {
        openAlert('Nenhuma distribuição foi definida.', 'warning');
        return;
      }

      // Chamar endpoint para salvar atribuições manuais
      await api.post(`/workspaces/addressable/organizations/${organizationId}/assignments`, {
        assignments: distributionsToSave,
      });

      openAlert(`${distributionsToSave.length} distribuição(ões) salva(s) com sucesso!`, 'success');
      setCurrentView('main');
    } catch (error) {
      console.error('Erro ao salvar distribuições:', error);
      openAlert('Erro ao salvar distribuições. Tente novamente.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Columns for Main View
  const mainViewColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Nome',
      width: 250,
    },
    {
      field: 'cpfBeneficiario',
      headerName: 'CPF do beneficiário',
      width: 180,
    },
    {
      field: 'cpfTitular',
      headerName: 'CPF do titular',
      width: 180,
    },
    {
      field: 'cargo',
      headerName: 'Cargo',
      width: 200,
    },
    {
      field: 'regiao',
      headerName: 'Região',
      width: 200,
    },
    {
      field: 'dataContratacao',
      headerName: 'Data de admissão',
      width: 180,
    },
    {
      field: 'assignedWorkspaceName',
      headerName: 'Distribuição',
      width: 300,
      valueFormatter: (value) => value || '-',
    },
  ];

  // Columns for Define Distribution View
  const defineDistributionColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Nome',
      width: 250,
    },
    {
      field: 'cpfBeneficiario',
      headerName: 'CPF do beneficiário',
      width: 180,
    },
    {
      field: 'cpfTitular',
      headerName: 'CPF do titular',
      width: 180,
    },
    {
      field: 'cargo',
      headerName: 'Cargo',
      width: 200,
    },
    {
      field: 'regiao',
      headerName: 'Região',
      width: 150,
    },
    {
      field: 'dataContratacao',
      headerName: 'Data de admissão',
      width: 180,
    },
    {
      field: 'distribuicao',
      headerName: 'Distribuição',
      width: 300,
      editable: true,
      type: 'singleSelect',
      valueOptions: (params) => {
        const beneficiary = params.row as IBeneficiary;
        return beneficiary.plans.map((plan) => ({ value: plan.id, label: plan.value }));
      },
      valueFormatter: (value) => {
        if (!value) return '-';
        // Find the plan label from the beneficiaries state
        const beneficiary = beneficiaries.find((b) =>
          b.plans.some((p) => p.id === value)
        );
        const plan = beneficiary?.plans.find((p) => p.id === value);
        return plan?.value || value;
      },
    },
  ];


  // Render Main View
  const renderMainView = () => (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ marginBottom: '24px' }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Gestão de endereçabilidade
        </Typography>
      </Box>

      {/* Cards de Status e Distribuição */}
      <Box sx={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
        <Card sx={{ flex: 1, boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, marginBottom: '16px' }}>
              Status Geral
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <Typography variant="body2">Definidos:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {statusGeral.definidos}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <Typography variant="body2">Aguardando definição:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {statusGeral.aguardandoDefinicao}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Total
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {statusGeral.total}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, marginBottom: '16px' }}>
              Distribuição
            </Typography>
            {distributions.map((dist, index) => (
              <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <Typography variant="body2">{dist.name}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {dist.count}
                </Typography>
              </Box>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Total
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {distributions.reduce((acc, d) => acc + d.count, 0)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Tabela de Beneficiários */}
      <Box sx={{ marginBottom: '24px', height: 400 }}>
        <DataGrid
          rows={beneficiaries}
          columns={mainViewColumns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[5, 10, 20, 50]}
          style={{ backgroundColor: 'white' }}
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        />
      </Box>

      {/* Footer com botões */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
        <Button variant="outlined" onClick={() => setCurrentView('defineDistribution')}>
          Definir distribuição
        </Button>
        <Button variant="outlined" onClick={() => setCurrentView('campaigns')}>
          Campanhas
        </Button>
      </Box>
    </Box>
  );

  // Render Define Distribution View
  const renderDefineDistributionView = () => (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '24px' }}>
        Definir distribuição
      </Typography>

      <Box sx={{ marginBottom: '24px', height: 600 }}>
        <DataGrid
          rows={beneficiariesToDefine}
          columns={defineDistributionColumns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[5, 10, 20, 50]}
          style={{ backgroundColor: 'white' }}
          processRowUpdate={(newRow) => {
            handleDistributionChange(newRow.id, newRow.distribuicao);
            return newRow;
          }}
          onProcessRowUpdateError={(error) => console.error('Erro ao atualizar linha:', error)}
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
        <Button variant="outlined" onClick={handleClearDistributions} disabled={isSaving}>
          Limpar
        </Button>
        <Button variant="contained" onClick={handleSaveDistributions} disabled={isSaving}>
          {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Salvar'}
        </Button>
      </Box>
    </Box>
  );

  // Render Campaigns View
  const renderCampaignsView = () => {
    if (!apiData?.beneficiaries) return null;

    return (
      <Box sx={{ width: '100%' }}>
        <div style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "center", marginBottom: '16px' }}>
          <IconButton
            size="small"
            onClick={getCampaignBackAction()}
          >
            <ArrowBack color="primary" />
          </IconButton>
          <Typography variant="h6">{getCampaignPageTitle()}</Typography>
        </div>

        {/* Só mostra as tabs se NÃO estiver na tela de detalhe */}
        {!isDetailPage && (
          <Container style={{ marginBottom: '24px' }}>
            <StyledButton
              variant={campaignTab === "SEND_CAMPAIGNS" ? "active" : "inactive"}
              onClick={() => setCampaignTab("SEND_CAMPAIGNS")}
            >
              Envio de campanha
            </StyledButton>
            <StyledButton
              variant={campaignTab === "CHECK_CAMPAIGNS" ? "active" : "inactive"}
              onClick={() => setCampaignTab("CHECK_CAMPAIGNS")}
            >
              Campanha em andamento
            </StyledButton>
          </Container>
        )}

        {/* Renderização condicional baseada no estado */}
        {!isDetailPage && campaignTab === "SEND_CAMPAIGNS" && (
          <AddressableCampaignSend
            beneficiaries={apiData.beneficiaries}
            onCampaignSent={handleCampaignSent}
          />
        )}
        {!isDetailPage && campaignTab === "CHECK_CAMPAIGNS" && (
          <AddressableCampaignStatus
            campaigns={campaigns}
            onCampaignSelect={setSelectedCampaign}
          />
        )}
        {isDetailPage && selectedCampaign && (
          <AddressableCampaignBeneficiaries campaign={selectedCampaign} />
        )}
      </Box>
    );
  };

  // Render Success View
  const renderSuccessView = () => (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px' }}>
      {/* Ilustração */}
      <Box
        sx={{
          width: '200px',
          height: '200px',
          marginBottom: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg width="200" height="200" viewBox="0 0 200 200" fill="none">
          <circle cx="100" cy="100" r="80" fill="#3024E1" opacity="0.1" />
          <path
            d="M70 100L90 120L130 80"
            stroke="#3024E1"
            strokeWidth="8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Box>

      <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '8px', textAlign: 'center' }}>
        Endereçabilidade confirmada com sucesso
      </Typography>
      <Typography variant="body1" sx={{ color: 'text.secondary', marginBottom: '32px', textAlign: 'center' }}>
        Os colaboradores foram definidos para operadoras
      </Typography>

      {/* Informações */}
      <Box sx={{ display: 'flex', gap: '48px', marginBottom: '32px' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', marginBottom: '4px' }}>
            Ação concluída em
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            09/07/2025
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', marginBottom: '4px' }}>
            Ação realizada por
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            João Ferreira
          </Typography>
        </Box>
      </Box>

      {/* Cards de Distribuição */}
      <Box sx={{ display: 'flex', gap: '16px', marginBottom: '32px', width: '100%', maxWidth: '800px' }}>
        {distributions.map((dist, index) => (
          <Card key={index} sx={{ flex: 1, boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.12)' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ marginBottom: '8px' }}>
                {dist.name}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {dist.count}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Botões */}
      <Box sx={{ display: 'flex', gap: '16px' }}>
        <Button variant="contained" onClick={() => setCurrentView('main')}>
          Retornar para atividade
        </Button>
        <Button variant="outlined">Ver histórico</Button>
      </Box>
    </Box>
  );

  // Loading state
  if (isFetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '48px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (isError) {
    // Verificar se é o erro específico de configuração não encontrada
    // Estrutura do erro: { statusCode: 404, data: { name: "NotFoundError", message: "..." } }
    console.log('Error object:', error); // Debug
    const errorMessage = (error as any)?.response?.data?.data?.message || (error as any)?.response?.data?.message || (error as any)?.message || '';
    console.log('Extracted error message:', errorMessage); // Debug
    const isConfigNotFound = errorMessage.includes('No workspace addressability was found');
    console.log('Is config not found:', isConfigNotFound); // Debug

    if (isConfigNotFound) {
      // Interface específica para configuração não encontrada
      return (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '48px',
          gap: '24px'
        }}>
          <Box
            sx={{
              width: '120px',
              height: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              backgroundColor: '#FFF4E5',
            }}
          >
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                fill="#F57C00"
              />
            </svg>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '8px', color: '#191c1a' }}>
              Grupo endereçável ainda não configurado
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: '400px' }}>
              Nenhuma configuração de endereçabilidade foi encontrada para esta organização.
              Configure os grupos de endereçabilidade antes de visualizar os beneficiários.
            </Typography>
          </Box>
        </Box>
      );
    }

    // Erro genérico
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '48px' }}>
        <Typography variant="body1" color="error">
          Erro ao carregar dados de endereçabilidade. Tente novamente mais tarde.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {currentView === 'main' && renderMainView()}
      {currentView === 'defineDistribution' && renderDefineDistributionView()}
      {currentView === 'campaigns' && renderCampaignsView()}
      {currentView === 'success' && renderSuccessView()}
    </>
  );
}
